import React from 'react'

import Player from './WebAudioPlayer'

import * as actions from './actionsNames'
import * as selectors from './playerSelectors'
import * as constants from './playerConstants'

import { TrackController } from '../../../utils/controllers/TrackController'

// singleton du Player
const player = new Player()

let autoNextTimeout = null  // statique
let autoNextCounter = 0     // statique

const AUTO_SUCCESSIVE_NEXT_MAX = 3
const AUTO_NEXT_DELAY = 4000


/* ********************************
 ************ @private *************
 ******************************** */

/**
 * Retourne le getFileUrl de la track en paramètre
 *
 * Struct du getFileUrl:
 *  bit_depth
 *! format_id
 *+ format (<=> format_id mais pour le desktop local)
 *  mime_type
 *! restrictions
 *! sample (true quand extrait seulement)
 *  sampling_rate
 *  track_id
 *+ album_id
 *+ status
 *! url
 *
 * Légende :
 * + Complété dans le state.localMusic par le desktop quand connu en cache ou import
 * ! Incomplet quand provient du state.localMusic, ou, pour ".url", lorsque la track est indisponible même en extrait sur l'API
 *
 * RG : https://docs.google.com/document/d/1YoiIe3rNCzYgmRFBgbouwX1RBg2D5T2dIk4NRfGOEY0 "Règles de gestion de lecture"
 *
 * @param trackData
 *
 * @return Promise : Object { ...getFileUrl, forbidden : true si interdit de lire le track demandé} | null (si aucun getFileUrl de track trouvée)
 * @private
 */
const _getFileUrl = (trackData) => {
	const trackId = trackData.id

	return new Promise((resolve) => {
		// On appelle l'API
		TrackController.fetchTrackUrl(trackId, 5, 'stream', false).then((file) => {
			if (file.url) {
				resolve(file)
			} else {
				resolve({
					...file,
					forbidden: true
				})
			}
		}).catch(() => {
			resolve(null)
		})
	})
}

/**
 * Récupère les informations de getFileUrl et toutes les informations de la track depuis l'API et le state.localMusic
 *
 * @param dispatch
 * @param trackData
 *
 * @return Object{ Object fullTrackData, Object fileUrl } | null
 * @private
 */
const _getTrack = (dispatch, trackData) => (
	_getFileUrl(trackData).then((fileUrl) => {
		let response
		if (fileUrl === null) {
			// track indisponible
			response = null
		} else {
			// track disponible
			response = {
				trackData,
				fileUrl // Si attribut forbidden:true, track interdite à la lecture, càd getFileUrl.url undefined
			}
		}
		return response
	})
)

/**
 * Transforme une tracksList de trackData en un array de [{id: x, originType: y, originId: z}, {...}] (une playqueue) puis dispatch
 *
 * @param dispatch
 * @param getState
 * @param tracksList
 * @param mode
 * @param originType
 * @param originId
 *
 * @private
 */
const _setPlayqueueFromTracksList = (dispatch, getState, tracksList, mode, originType, originId) => {
	const playqueue = tracksList.map((trackData) => (
		{
			originType,
			originId,
			id: trackData.id
		}
	))
	if (mode === constants.PLAYQUEUE_FEED_MODE.complete) {
		dispatch({
			type: actions.SET_PLAYQUEUE_END,
			playqueue
		})
	} else if (mode === constants.PLAYQUEUE_FEED_MODE.replace) {
		dispatch({
			type: actions.SET_PLAYQUEUE,
			playqueue
		})
	}
}

/**
 *
 * @param dispatch
 * @param getState
 *
 * @uses player
 *
 * @private
 */
const _prepareNextTrack = (dispatch, getState) => {
	const state = getState()
	player.resetNextTrack()
	const nextTrackData = selectors.getNextPlayqueueTrack(state)
	if (nextTrackData !== false) {
		_getTrack(dispatch, nextTrackData).then((response) => {
			if (response !== null && !response.fileUrl.forbidden) {
				player.prepareNextTrack(response.trackData, response.fileUrl)
			}
		})
	}
}

/* **********************************************
 *************************************************
 ********************************************** */


/**
 *
 * @uses player
 */
export const nextTrack = () => (dispatch, getState) => {
	const nextTrackData = selectors.getNextPlayqueueTrack(getState())
	if (nextTrackData !== false) {
		const state = getState()
		player.closeMusic()
		const nextIndex = selectors.getNextTrackIndex(state)
		// playTrack(null, nextIndex, true)(dispatch, getState)
		// Remarque, équivalent à :
		dispatch(playTrack(null, nextIndex, true))
	} else {
		player.seek(0)
		player.pause()
	}
}

/**
 * Lance la lecture d'une track au sein de la playqueue, avec redéfinition ou non de la playqueue, et dispatch
 *
 * @param tracksList | null (on lit dans le state) ou Array de trackData
 * @param indexToRead
 * @param autoPlay
 * @param originType | cf playerConstants.js / requis uniquement si tracksList!==null
 * @param originId                           / requis uniquement si tracksList!==null
 *
 * @uses player
 */
export const playTrack = (tracksList, indexToRead, autoPlay, originType, originId) => (dispatch, getState) => {
	const state = getState()
	const currentIndex = selectors.getPlayqueueIndex(state)

	// La première chose à faire si on autoPlay : stop de la track courante
	if (currentIndex > -1 && autoPlay === true) {
		player.closeMusic()
	}

	// Si définition d'une nouvelle tracksList
	if (tracksList !== null) {
		_setPlayqueueFromTracksList(dispatch, getState, tracksList, constants.PLAYQUEUE_FEED_MODE.replace, originType, originId)
	}

	// On positionne le curseur de lecture dans la playqueue
	dispatch({
		type: actions.SET_MOVE_IN_PLAYQUEUE,
		indexToRead
	})

	let trackData
	if (tracksList !== null) {
		trackData = tracksList[indexToRead]
	} else {
		trackData = selectors.getPlayqueueTrack(state)(indexToRead)
	}
	_getTrack(dispatch, trackData).then((response) => {
		if (response !== null) {
			dispatch({
				type: actions.SET_CURRENT_GETFILEURL,
				getFileUrl: response.fileUrl
			})

			if (!response.fileUrl.forbidden) {
				autoNextCounter = 0 // Remet à 0 un éventuel autonext qui avait commencé (else ci dessous)
				// _overwriteReportStreaming(response.trackData, response.fileUrl)
				player.load(response.trackData, response.fileUrl, autoPlay, 0)

			} else if (response.fileUrl.forbidden === true) {
				// Gérer l'autonext si le track est totalement indisponible à l'écoute
				autoNextCounter++
				if (autoNextTimeout) {
					clearTimeout(autoNextTimeout)
				}
				if (autoNextCounter < AUTO_SUCCESSIVE_NEXT_MAX) {
					autoNextTimeout = setTimeout(
						() => {
							dispatch(nextTrack())
						},
						AUTO_NEXT_DELAY
					)
				}
			}
		}
	})
}

/**
 *
 * Remplace la playqueue.
 *
 * @param tracksList
 * @param originType
 * @param originId
 */
export const changePlayqueue = (tracksList, originType, originId) => (dispatch, getState) => {
	_setPlayqueueFromTracksList(dispatch, getState, tracksList, constants.PLAYQUEUE_FEED_MODE.replace, originType, originId, true)
	dispatch(playTrack(null, 0, true))
}

/**
 *
 * Ajoute à la fin. Si aucun track n'était en cours de lecture (<=> playqueue vide), on lit le premier track de la nouvelle queue.
 *
 * @param tracksList
 * @param originType
 * @param originId
 */
export const completePlayqueue = (tracksList, originType, originId) => (dispatch, getState) => {
	_setPlayqueueFromTracksList(dispatch, getState, tracksList, constants.PLAYQUEUE_FEED_MODE.complete, originType, originId, true)
	const currentIndex = selectors.getPlayqueueIndex(getState())
	if (currentIndex === -1) {
		dispatch(playTrack(null, 0, true))
	} else {
		_prepareNextTrack(dispatch, getState)
	}
}

/**
 * vide la liste de lecture
 */
export const clearPlayqueue = () => (dispatch) => {

	player.closeMusic()
	dispatch({
		type: actions.CLEAR_PLAYQUEUE
	})
}


/**
 *
 */
export const clearAll = () => (dispatch) => {
	dispatch(clearPlayqueue())
	player.closeMusic()
}

/* Concernant play, pause, et seek, les dispatch ont lieu dans les callbacks du player, plus bas */


export const preload = () => {
	player.preload()
}
/**
 *
 * @uses player
 */
export const play = () => () => {
	player.play()
}
/**
 *
 * @uses player
 */
export const pause = () => () => {
	player.pause()
}


/* *********************************
 ****** AUDIO PLAYER CALLBACKS ******
 ********************************* */


let playerStore = {
	dispatch: () => {
		console.log('!!! AudioPlayer Store dispatch is not initialized !!!')
	},
	getState: () => {
		console.log('!!! AudioPlayer Store getState is not initialized !!!')
	}
}
export const registerAudioPlayerStore = (store) => {
	playerStore = store
}

const _onLoadedTrack = (trackInformation) => {
	playerStore.dispatch({
		type: actions.SET_LOADED,
		loaded: true,
		trackInformation
	})
}

const _onPlayedTrack = () => {
	// _playReportStreaming()
	playerStore.dispatch({
		type: actions.SET_PLAYING,
		playing: true
	})
}

const _onPausedTrack = () => {
	// _pauseReportStreaming()
	playerStore.dispatch({
		type: actions.SET_PLAYING,
		playing: false
	})
}

const _onBufferingTrack = () => {
	playerStore.dispatch({
		type: actions.SET_BUFFERING,
		buffering: true
	})
}

const _onBufferingEndedTrack = () => {
	playerStore.dispatch({
		type: actions.SET_BUFFERING,
		buffering: false
	})
}

const _onBufferChangedTrack = (buffers) => {
	playerStore.dispatch({
		type: actions.SET_BUFFERS,
		buffers
	})
}

const _onCacheComplete = () => {

}

const _onPreparedNextTrack = () => {

}

const _onSwitchedNextTrack = (switchedToNext) => {
	if (switchedToNext) {
		const nextIndex = selectors.getNextTrackIndex(playerStore.getState())
		if (nextIndex !== false) {
			// _overwriteReportStreaming(switchedToNext.trackData, switchedToNext.fileUrl)
			playerStore.dispatch({
				type: actions.SET_MOVE_IN_PLAYQUEUE,
				indexToRead: nextIndex
			})
		}
	}
}

const _onProgressChanged = (second) => {
	playerStore.dispatch({
		type: actions.SET_SEEK,
		seek: second
	})
}

const _onTrackEndComing = () => {
	_prepareNextTrack(playerStore.dispatch, playerStore.getState)
}

const _onTrackEnded = () => {

	if (!player.switchNextTrack()) {
		playerStore.dispatch(nextTrack())
	}
	if (!selectors.canDoNext(playerStore.getState())) {
		playerStore.dispatch(clearPlayqueue())
	}
}


player.setOnLoadedTrack(_onLoadedTrack)
player.setOnPlayedTrack(_onPlayedTrack)
player.setOnPausedTrack(_onPausedTrack)
player.setOnBufferingTrack(_onBufferingTrack)
player.setOnBufferingEndedTrack(_onBufferingEndedTrack)
player.setOnBufferChangedTrack(_onBufferChangedTrack)
player.setOnCacheComplete(_onCacheComplete)
player.setOnPreparedNextTrack(_onPreparedNextTrack)
player.setOnSwitchedNextTrack(_onSwitchedNextTrack)
player.setOnProgressChanged(_onProgressChanged)
player.setOnTrackEndComing(_onTrackEndComing)
player.setOnTrackEnded(_onTrackEnded)



// WEBPACK FOOTER //
// ./src/components/miniPlayer/AudioPlayer/playerActions.js